import { CABINET_BOXES, SHAPE_PART_ID, SQFT_FEATURES } from "../../../../../../constants";
import {
  canShapeBeAutoShifted,
  getIsShapePlacedOnTheFloor,
  getIsShapePlacedOnTheWall,
} from "../../../../../../helpers/shapeHelper";
import {
  canCabinetBoxesShareRun,
  isShapeTallCabinet,
} from "../../../../../../helpers/cabinetBoxCompatibilityHelper";
import sqftConfig from "../../../../../canvas/store/sqftConfig";
import sqftShapeManager from "../../../../../canvas/store/sqftShapeManager";
import workspace from "../../../../../canvas/core/workspace";
import { CABINET_LINE_ROLE } from "../../../../../canvas/core/shapes/shapeParts/shapeCabinet";
import { createCabinetSnapDebugLogger } from "./cabinetPlacementDebug";

const AXIS_TOLERANCE = 0.5;
const MIN_OVERLAP_RATIO = 0.35;
// 3D cursor movement is less precise than the 2D canvas, so keep cabinet snap
// close enough to feel intentional without grabbing cabinets from far away.
const CABINET_PLACEMENT_SNAP_OFFSET = 4;
// Corner wall cabinets need a wider secondary pull: once the first back wall is
// selected, the second back wall should be allowed to complete the corner.
const CABINET_CORNER_BACK_SNAP_OFFSET = 24;

// 3D drag resolves intent only; commit code decides how to apply it.
// INSERT means "make room in an existing run", SNAP means "attach to a free edge".
export const CABINET_PLACEMENT_TYPE = {
  INSERT: 'insert',
  SNAP: 'snap',
};

function moveBounds(bounds, delta) {
  return {
    left: bounds.left + delta.x,
    right: bounds.right + delta.x,
    top: bounds.top + delta.y,
    bottom: bounds.bottom + delta.y,
  };
}

function getOverlap(firstMin, firstMax, secondMin, secondMax) {
  return Math.max(0, Math.min(firstMax, secondMax) - Math.max(firstMin, secondMin));
}

function getBoundsOverlapArea(firstBounds, secondBounds) {
  const xOverlap = getOverlap(firstBounds.left, firstBounds.right, secondBounds.left, secondBounds.right);
  const yOverlap = getOverlap(firstBounds.top, firstBounds.bottom, secondBounds.top, secondBounds.bottom);

  return xOverlap * yOverlap;
}

function getOverlapRatio(overlap, size) {
  return size > 0 ? overlap / size : 0;
}

// Top cabinets can have short side edges. Use the smaller overlap span so a
// valid short side-to-side snap is not rejected just because the moving cabinet
// is much wider than the target edge.
function hasEnoughOverlap(firstMin, firstMax, secondMin, secondMax) {
  const normalizedFirstMin = Math.min(firstMin, firstMax);
  const normalizedFirstMax = Math.max(firstMin, firstMax);
  const normalizedSecondMin = Math.min(secondMin, secondMax);
  const normalizedSecondMax = Math.max(secondMin, secondMax);
  const overlap = getOverlap(normalizedFirstMin, normalizedFirstMax, normalizedSecondMin, normalizedSecondMax);
  const firstSize = Math.abs(normalizedFirstMax - normalizedFirstMin);
  const secondSize = Math.abs(normalizedSecondMax - normalizedSecondMin);
  const overlapBase = Math.min(firstSize, secondSize);

  return getOverlapRatio(overlap, overlapBase) >= MIN_OVERLAP_RATIO;
}

function isSameCabinetFamily(first, second) {
  const firstPartId = CABINET_BOXES.includes(first.partId) ? first.partId : first.parentPartId;
  const secondPartId = CABINET_BOXES.includes(second.partId) ? second.partId : second.parentPartId;

  if (CABINET_BOXES.includes(firstPartId) && CABINET_BOXES.includes(secondPartId)) {
    return canCabinetBoxesShareRun(first, second);
  }

  return first.partId === second.partId
    || first.partId === second.parentPartId
    || (first.parentPartId && first.parentPartId === second.partId);
}

function canResolveCabinetPlacement(movingShape, targetShape) {
  return canShapeBeAutoShifted(movingShape, targetShape)
    || canCabinetBoxesShareRun(movingShape, targetShape);
}

function isWallCabinetShape(shape) {
  return getIsShapePlacedOnTheWall(shape)
    || shape.partId === SHAPE_PART_ID.top_cabinet
    || shape.parentPartId === SHAPE_PART_ID.top_cabinet
    || shape.partId === SQFT_FEATURES.top_cabinet
    || shape.parentPartId === SQFT_FEATURES.top_cabinet
    || shape.partId === SQFT_FEATURES.wall_vanity_cabinet
    || shape.parentPartId === SQFT_FEATURES.wall_vanity_cabinet;
}

function isFloorCabinetShape(shape) {
  return getIsShapePlacedOnTheFloor(shape)
    || shape.partId === SHAPE_PART_ID.bottom_cabinet
    || shape.parentPartId === SHAPE_PART_ID.bottom_cabinet
    || shape.partId === SQFT_FEATURES.bottom_cabinet
    || shape.parentPartId === SQFT_FEATURES.bottom_cabinet;
}

function canSnapCabinetBackToBack(movingShape, targetShape) {
  return (
    isWallCabinetShape(movingShape)
    && isFloorCabinetShape(targetShape)
  ) || (
    isFloorCabinetShape(movingShape)
    && isWallCabinetShape(targetShape)
  );
}

function getCompatibleShapes(movingShape) {
  return workspace.getShapes(true)
    .filter(shape => (
      shape
      && shape.id !== movingShape.id
      && typeof shape.getLines === 'function'
      && !shape.isHidden
      && (
        (isSameCabinetFamily(movingShape, shape) && canResolveCabinetPlacement(movingShape, shape))
        || canSnapCabinetBackToBack(movingShape, shape)
        || (isWallCabinetShape(movingShape) && isWallCabinetShape(shape))
      )
    ));
}

export function createCabinetPlacementContext(movingShape) {
  // A drag cannot change which existing cabinets are compatible targets until
  // commit. Cache that list and their bounds once so every move frame only
  // evaluates candidate distances.
  return {
    compatibleShapeEntries: getCompatibleShapes(movingShape)
      .map(shape => ({
        shape,
        bounds: sqftShapeManager.getShapeBoundsOnWorkspace(shape),
      }))
      .filter(entry => entry.bounds),
  };
}

function getCandidateOverlapArea(candidate, compatibleShapeEntries) {
  if (!isShapeTallCabinet(candidate.movingShape)) {
    return 0;
  }

  // Tall cabinets can snap to either top or bottom runs. Prefer the candidate
  // that leaves the least overlap with the opposite lane.
  const candidateBounds = moveBounds(candidate.movingBounds, candidate.delta);

  return compatibleShapeEntries.reduce((overlapArea, entry) => {
    const { shape, bounds } = entry;

    if (!shape || shape.id === candidate.targetShape.id) {
      return overlapArea;
    }

    return overlapArea + getBoundsOverlapArea(candidateBounds, bounds);
  }, 0);
}

function getLinePointsOnWorkspace(line) {
  return {
    from: line.from.getPointPosOnWorkspace(),
    to: line.to.getPointPosOnWorkspace(),
  };
}

function getLineMidpoint(linePoints) {
  return {
    x: (linePoints.from.x + linePoints.to.x) / 2,
    y: (linePoints.from.y + linePoints.to.y) / 2,
  };
}

function getLineOrientation(linePoints) {
  if (Math.abs(linePoints.from.x - linePoints.to.x) <= AXIS_TOLERANCE) {
    return 'vertical';
  }

  if (Math.abs(linePoints.from.y - linePoints.to.y) <= AXIS_TOLERANCE) {
    return 'horizontal';
  }

  return null;
}

function getLineOverlapWithRange(lineMin, lineMax, rangeMin, rangeMax) {
  return getOverlap(Math.min(lineMin, lineMax), Math.max(lineMin, lineMax), rangeMin, rangeMax);
}

function getFrontLinesOnWorkspace(shape) {
  if (
    !shape.shapeCabinet
    || typeof shape.shapeCabinet.getFrontLinesOnWorkspace !== 'function'
  ) {
    return [];
  }

  return shape.shapeCabinet.getFrontLinesOnWorkspace();
}

function getExplicitBackLinesOnWorkspace(shape) {
  if (
    !shape.shapeCabinet
    || typeof shape.shapeCabinet.getBackLinesOnWorkspace !== 'function'
  ) {
    return [];
  }

  return shape.shapeCabinet.getBackLinesOnWorkspace();
}

function getExplicitSideLinesOnWorkspace(shape) {
  if (
    !shape.shapeCabinet
    || typeof shape.shapeCabinet.getSideLinesOnWorkspace !== 'function'
  ) {
    return [];
  }

  return shape.shapeCabinet.getSideLinesOnWorkspace();
}

function getExplicitLinesOnWorkspaceByRole(shape, role) {
  if (role === CABINET_LINE_ROLE.BACK) {
    return getExplicitBackLinesOnWorkspace(shape);
  }

  if (role === CABINET_LINE_ROLE.SIDE) {
    return getExplicitSideLinesOnWorkspace(shape);
  }

  return [];
}

function isBackLineForShape(linePoints, frontLineEntries) {
  const lineOrientation = getLineOrientation(linePoints);
  const midpoint = getLineMidpoint(linePoints);

  return frontLineEntries.some((frontLineEntry) => {
    if (lineOrientation !== frontLineEntry.orientation) {
      return false;
    }

    const frontVector = {
      x: frontLineEntry.midpoint.x - frontLineEntry.shapeCenter.x,
      y: frontLineEntry.midpoint.y - frontLineEntry.shapeCenter.y,
    };
    const lineVector = {
      x: midpoint.x - frontLineEntry.shapeCenter.x,
      y: midpoint.y - frontLineEntry.shapeCenter.y,
    };

    return (frontVector.x * lineVector.x) + (frontVector.y * lineVector.y) < 0;
  });
}

function getFrontLineEntriesFromLines(frontLines) {
  return frontLines
    .map(frontLine => ({
      orientation: getLineOrientation(frontLine),
      midpoint: getLineMidpoint(frontLine),
      shapeCenter: frontLine.shapeCenter,
    }))
    .filter(entry => entry.orientation && entry.shapeCenter);
}

function createWorkspacePoint(point) {
  return {
    getPointPosOnWorkspace: () => point,
  };
}

function createPreviewLine(from, to) {
  return {
    from: createWorkspacePoint(from),
    to: createWorkspacePoint(to),
  };
}

function getCabinetLineEntries(shape, role) {
  const frontLines = getFrontLinesOnWorkspace(shape);
  const frontLineEntries = getFrontLineEntriesFromLines(frontLines);

  return getExplicitLinesOnWorkspaceByRole(shape, role)
    .map(roleLine => ({
      role,
      line: createPreviewLine(roleLine.from, roleLine.to),
      points: {
        from: roleLine.from,
        to: roleLine.to,
      },
      orientation: getLineOrientation(roleLine),
      previewOffsetDirection: role === CABINET_LINE_ROLE.BACK
        ? getBackLinePreviewOffsetDirection(roleLine, frontLineEntries)
        : null,
    }))
    .filter(entry => entry.orientation);
}

function getCabinetLineEntriesByRoles(shape, roles) {
  return roles.reduce((entries, role) => ([
    ...entries,
    ...getCabinetLineEntries(shape, role),
  ]), []);
}

function getBackLinePreviewOffsetDirection(linePoints, frontLineEntries) {
  const midpoint = getLineMidpoint(linePoints);
  const matchingFrontLine = frontLineEntries.find(frontLineEntry => (
    frontLineEntry.orientation === getLineOrientation(linePoints)
  ));

  if (!matchingFrontLine) {
    return { x: 0, y: 0 };
  }

  const x = midpoint.x - matchingFrontLine.shapeCenter.x;
  const y = midpoint.y - matchingFrontLine.shapeCenter.y;
  const length = Math.hypot(x, y);

  if (length <= AXIS_TOLERANCE) {
    return { x: 0, y: 0 };
  }

  return {
    x: x / length,
    y: y / length,
  };
}

function isCabinetBackLine(shape, line) {
  if (!shape || !line) {
    return false;
  }

  const linePoints = getLinePointsOnWorkspace(line);
  const frontLineEntries = getFrontLineEntriesFromLines(getFrontLinesOnWorkspace(shape));

  return frontLineEntries.length > 0
    && isBackLineForShape(linePoints, frontLineEntries);
}

function moveLinePoints(linePoints, delta) {
  return {
    from: {
      x: linePoints.from.x + delta.x,
      y: linePoints.from.y + delta.y,
    },
    to: {
      x: linePoints.to.x + delta.x,
      y: linePoints.to.y + delta.y,
    },
  };
}

function getLineAxisValue(linePoints, orientation) {
  return orientation === 'vertical' ? linePoints.from.x : linePoints.from.y;
}

function getLineOverlapForOrientation(firstLinePoints, secondLinePoints, orientation) {
  return orientation === 'vertical'
    ? hasEnoughOverlap(firstLinePoints.from.y, firstLinePoints.to.y, secondLinePoints.from.y, secondLinePoints.to.y)
    : hasEnoughOverlap(firstLinePoints.from.x, firstLinePoints.to.x, secondLinePoints.from.x, secondLinePoints.to.x);
}

function getPlacementPriority(candidate) {
  if (candidate.type === CABINET_PLACEMENT_TYPE.INSERT) {
    return 0;
  }

  if (candidate.edge && candidate.edge.role === 'back-wall') {
    return 1;
  }

  if (candidate.edge && candidate.edge.role === 'side-wall') {
    return 2;
  }

  return 3;
}

function normalizeRotationDegrees(rotationDegrees) {
  const normalized = rotationDegrees % 360;

  return normalized < 0 ? normalized + 360 : normalized;
}

function rotateShapeToDegrees(shape, rotationDegrees) {
  if (!shape || typeof shape.rotate !== 'function') {
    return;
  }

  const currentRotation = normalizeRotationDegrees(shape.rotatedDegrees || 0);
  const nextRotation = normalizeRotationDegrees(rotationDegrees || 0);
  const rotationDelta = nextRotation - currentRotation;

  if (Math.abs(rotationDelta) <= AXIS_TOLERANCE) {
    return;
  }

  shape.rotate(rotationDelta, shape.Direction.LEFT, false);
}

function withShapeRotation(shape, rotationDegrees, callback) {
  const originalRotation = shape ? shape.rotatedDegrees || 0 : 0;

  rotateShapeToDegrees(shape, rotationDegrees);

  try {
    return callback();
  } finally {
    rotateShapeToDegrees(shape, originalRotation);
  }
}

export function applyCabinetLineSnapPlacement(shape, placement) {
  if (!shape || !placement || !placement.position) {
    return;
  }

  if (typeof placement.rotationDegrees === 'number') {
    rotateShapeToDegrees(shape, placement.rotationDegrees);
  }

  shape.setPosition(placement.position);
  if (shape.shapeAbsolute) {
    shape.shapeAbsolute.setAbsolutePos(placement.position);
  }
}

function getBackWallSideSnapDelta({
  movingShape,
  targetShape,
  movingBounds,
  movingDelta,
  nextDelta,
  primaryOrientation,
  compatibleShapeEntries,
}) {
  const correctionDelta = {
    x: nextDelta.x - movingDelta.x,
    y: nextDelta.y - movingDelta.y,
  };
  const candidateBounds = moveBounds(movingBounds, correctionDelta);
  let closestDelta = null;

  compatibleShapeEntries.forEach(({ shape, bounds }) => {
    if (
      !shape
      || !bounds
      || shape.id === movingShape.id
      || shape.id === targetShape.id
      || !isWallCabinetShape(shape)
    ) {
      return;
    }

    if (primaryOrientation === 'horizontal') {
      if (!hasEnoughOverlap(candidateBounds.top, candidateBounds.bottom, bounds.top, bounds.bottom)) {
        return;
      }

      [
        bounds.left - candidateBounds.right,
        bounds.right - candidateBounds.left,
      ].forEach((snapDelta) => {
        if (Math.abs(snapDelta) > CABINET_PLACEMENT_SNAP_OFFSET) {
          return;
        }

        if (closestDelta && Math.abs(closestDelta.x) <= Math.abs(snapDelta)) {
          return;
        }

        closestDelta = { x: snapDelta, y: 0 };
      });
      return;
    }

    if (!hasEnoughOverlap(candidateBounds.left, candidateBounds.right, bounds.left, bounds.right)) {
      return;
    }

    [
      bounds.top - candidateBounds.bottom,
      bounds.bottom - candidateBounds.top,
    ].forEach((snapDelta) => {
      if (Math.abs(snapDelta) > CABINET_PLACEMENT_SNAP_OFFSET) {
        return;
      }

      if (closestDelta && Math.abs(closestDelta.y) <= Math.abs(snapDelta)) {
        return;
      }

      closestDelta = { x: 0, y: snapDelta };
    });
  });

  return closestDelta;
}

function getClosestSnapDelta(values) {
  const snapDeltas = values
    .filter(value => Math.abs(value) <= CABINET_PLACEMENT_SNAP_OFFSET)
    .sort((first, second) => Math.abs(first) - Math.abs(second));

  return snapDeltas.length ? snapDeltas[0] : null;
}

function getSecondaryBoundsAlignmentDelta({
  movingBounds,
  targetBounds,
  movingDelta,
  nextDelta,
  primaryOrientation,
}) {
  const correctionDelta = {
    x: nextDelta.x - movingDelta.x,
    y: nextDelta.y - movingDelta.y,
  };
  const candidateBounds = moveBounds(movingBounds, correctionDelta);

  if (primaryOrientation === 'vertical') {
    const snapDelta = getClosestSnapDelta([
      targetBounds.top - candidateBounds.top,
      targetBounds.bottom - candidateBounds.bottom,
    ]);

    return snapDelta === null ? null : { x: 0, y: snapDelta };
  }

  const snapDelta = getClosestSnapDelta([
    targetBounds.left - candidateBounds.left,
    targetBounds.right - candidateBounds.right,
  ]);

  return snapDelta === null ? null : { x: snapDelta, y: 0 };
}

function findTargetEdgeLine(targetShape, targetBounds, edge) {
  const lines = targetShape.getLines();

  return lines.find((line) => {
    const { from, to } = getLinePointsOnWorkspace(line);

    if (edge.orientation === 'vertical') {
      const isVertical = Math.abs(from.x - to.x) <= AXIS_TOLERANCE;
      const isSameEdge = Math.abs(from.x - edge.x) <= AXIS_TOLERANCE;
      const hasOverlap = getLineOverlapWithRange(from.y, to.y, targetBounds.top, targetBounds.bottom) > 0;

      return isVertical && isSameEdge && hasOverlap;
    }

    const isHorizontal = Math.abs(from.y - to.y) <= AXIS_TOLERANCE;
    const isSameEdge = Math.abs(from.y - edge.y) <= AXIS_TOLERANCE;
    const hasOverlap = getLineOverlapWithRange(from.x, to.x, targetBounds.left, targetBounds.right) > 0;

    return isHorizontal && isSameEdge && hasOverlap;
  }) || null;
}

function buildCandidate({
  movingShape,
  movingBounds,
  candidatePosition,
  targetShape,
  targetBounds,
  distance,
  delta,
  edge,
  newPosition,
  placementMode,
}) {
  const hoveredLine = findTargetEdgeLine(targetShape, targetBounds, edge);
  if (!hoveredLine) {
    return null;
  }

  const insertPoint = edge.orientation === 'vertical'
    ? { x: edge.x, y: targetBounds.top }
    : { x: targetBounds.left, y: edge.y };
  const isTargetBackLine = isCabinetBackLine(targetShape, hoveredLine);
  const canInsert = placementMode === CABINET_PLACEMENT_TYPE.INSERT || placementMode === 'auto';
  const hasAdjacentShape = canInsert
    && !isTargetBackLine
    && movingShape.shapeAutoShuffle
    && movingShape.shapeAutoShuffle.checkForAdjacentShape(targetShape, insertPoint, hoveredLine);

  if (placementMode === CABINET_PLACEMENT_TYPE.INSERT && (!hasAdjacentShape || isTargetBackLine)) {
    return null;
  }

  return {
    type: hasAdjacentShape ? CABINET_PLACEMENT_TYPE.INSERT : CABINET_PLACEMENT_TYPE.SNAP,
    position: newPosition,
    insertPoint,
    hoveredLine,
    targetShape,
    targetBounds,
    movingShape,
    movingBounds,
    candidatePosition,
    distance,
    delta,
    edge,
  };
}

// Looks for vertical side-to-side cabinet placement, equivalent to dragging a
// cabinet near another cabinet's left or right edge in 2D.
function getVerticalEdgeCandidates(movingShape, movingBounds, candidatePosition, targetShape, targetBounds) {
  if (!hasEnoughOverlap(movingBounds.top, movingBounds.bottom, targetBounds.top, targetBounds.bottom)) {
    return [];
  }

  const snapToLeft = targetBounds.left - movingBounds.right;
  const snapToRight = targetBounds.right - movingBounds.left;
  const insertAtLeft = targetBounds.left - movingBounds.left;

  return [
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(snapToLeft),
      delta: { x: snapToLeft, y: 0 },
      edge: { orientation: 'vertical', x: targetBounds.left },
      newPosition: { x: candidatePosition.x + snapToLeft, y: candidatePosition.y },
      placementMode: CABINET_PLACEMENT_TYPE.SNAP,
    }),
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(snapToRight),
      delta: { x: snapToRight, y: 0 },
      edge: { orientation: 'vertical', x: targetBounds.right },
      newPosition: { x: candidatePosition.x + snapToRight, y: candidatePosition.y },
      placementMode: 'auto',
    }),
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(insertAtLeft),
      delta: { x: insertAtLeft, y: 0 },
      edge: { orientation: 'vertical', x: targetBounds.left },
      newPosition: { x: candidatePosition.x + insertAtLeft, y: candidatePosition.y },
      placementMode: CABINET_PLACEMENT_TYPE.INSERT,
    }),
  ].filter(Boolean);
}

// Looks for horizontal top/bottom cabinet placement for cabinet runs rotated
// around the workspace.
function getHorizontalEdgeCandidates(movingShape, movingBounds, candidatePosition, targetShape, targetBounds) {
  if (!hasEnoughOverlap(movingBounds.left, movingBounds.right, targetBounds.left, targetBounds.right)) {
    return [];
  }

  const snapToTop = targetBounds.top - movingBounds.bottom;
  const snapToBottom = targetBounds.bottom - movingBounds.top;
  const insertAtTop = targetBounds.top - movingBounds.top;

  return [
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(snapToTop),
      delta: { x: 0, y: snapToTop },
      edge: { orientation: 'horizontal', y: targetBounds.top },
      newPosition: { x: candidatePosition.x, y: candidatePosition.y + snapToTop },
      placementMode: CABINET_PLACEMENT_TYPE.SNAP,
    }),
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(snapToBottom),
      delta: { x: 0, y: snapToBottom },
      edge: { orientation: 'horizontal', y: targetBounds.bottom },
      newPosition: { x: candidatePosition.x, y: candidatePosition.y + snapToBottom },
      placementMode: 'auto',
    }),
    buildCandidate({
      movingShape,
      movingBounds,
      candidatePosition,
      targetShape,
      targetBounds,
      distance: Math.abs(insertAtTop),
      delta: { x: 0, y: insertAtTop },
      edge: { orientation: 'horizontal', y: targetBounds.top },
      newPosition: { x: candidatePosition.x, y: candidatePosition.y + insertAtTop },
      placementMode: CABINET_PLACEMENT_TYPE.INSERT,
    }),
  ].filter(Boolean);
}

function buildWallBackSnapCandidate({
  movingShape,
  movingBounds,
  candidatePosition,
  targetShape,
  targetBounds,
  movingBackLineEntry,
  targetBackLineEntry,
  movingBackLineEntries,
  targetBackLineEntries,
  compatibleShapeEntries,
  movingDelta,
  debugLogger,
}) {
  const orientation = targetBackLineEntry.orientation;
  const targetPoints = targetBackLineEntry.points;
  const movedPoints = moveLinePoints(movingBackLineEntry.points, movingDelta);
  const targetLineValue = getLineAxisValue(targetPoints, orientation);
  const movedLineValue = getLineAxisValue(movedPoints, orientation);
  const snapDelta = targetLineValue - movedLineValue;
  let nextDelta = orientation === 'vertical'
    ? { x: movingDelta.x + snapDelta, y: movingDelta.y }
    : { x: movingDelta.x, y: movingDelta.y + snapDelta };
  let nextMovedPoints = moveLinePoints(movingBackLineEntry.points, nextDelta);
  const primaryOverlap = getLineOverlapForOrientation(nextMovedPoints, targetPoints, orientation);
  const orthogonalDelta = getOrthogonalBackSnapDelta({
    movingBackLineEntry,
    targetBackLineEntry,
    movingBackLineEntries,
    targetBackLineEntries,
    nextDelta,
  });
  if (orthogonalDelta) {
    nextDelta = {
      x: nextDelta.x + orthogonalDelta.x,
      y: nextDelta.y + orthogonalDelta.y,
    };
    nextMovedPoints = moveLinePoints(movingBackLineEntry.points, nextDelta);
  }
  const sideSnapDelta = orthogonalDelta
    ? null
    : getBackWallSideSnapDelta({
      movingShape,
      targetShape,
      movingBounds,
      movingDelta,
      nextDelta,
      primaryOrientation: orientation,
      compatibleShapeEntries,
    });
  if (sideSnapDelta) {
    nextDelta = {
      x: nextDelta.x + sideSnapDelta.x,
      y: nextDelta.y + sideSnapDelta.y,
    };
    nextMovedPoints = moveLinePoints(movingBackLineEntry.points, nextDelta);
  }

  const finalOverlap = getLineOverlapForOrientation(nextMovedPoints, targetPoints, orientation);
  if (debugLogger) {
    debugLogger.addLineEvaluation({
      movingLineEntry: movingBackLineEntry,
      targetLineEntry: targetBackLineEntry,
      orientation,
      movedLineValue,
      targetLineValue,
      snapDelta,
      primaryOverlap,
      orthogonalDelta,
      finalOverlap,
      movedLinePoints: nextMovedPoints,
      targetLinePoints: targetPoints,
    });
  }

  if (!finalOverlap) {
    return null;
  }

  return {
    type: CABINET_PLACEMENT_TYPE.SNAP,
    rotationDegrees: targetShape.rotatedDegrees || 0,
    position: {
      x: candidatePosition.x + nextDelta.x - movingDelta.x,
      y: candidatePosition.y + nextDelta.y - movingDelta.y,
    },
    insertPoint: orientation === 'vertical'
      ? { x: targetLineValue, y: targetBounds.top }
      : { x: targetBounds.left, y: targetLineValue },
    hoveredLine: targetBackLineEntry.line,
    targetShape,
    targetBounds,
    movingShape,
    movingBounds,
    candidatePosition,
    distance: Math.max(
      Math.abs(snapDelta),
      sideSnapDelta ? Math.abs(sideSnapDelta.x + sideSnapDelta.y) : 0
    ),
    delta: {
      x: nextDelta.x - movingDelta.x,
      y: nextDelta.y - movingDelta.y,
    },
    edge: orientation === 'vertical'
      ? {
        orientation,
        x: targetLineValue,
        role: 'back-wall',
        previewOffsetDirection: targetBackLineEntry.previewOffsetDirection,
        secondaryLine: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.line
          : null,
        secondaryOrientation: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.orientation
          : null,
        secondaryPreviewOffsetDirection: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.previewOffsetDirection
          : null,
      }
      : {
        orientation,
        y: targetLineValue,
        role: 'back-wall',
        previewOffsetDirection: targetBackLineEntry.previewOffsetDirection,
        secondaryLine: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.line
          : null,
        secondaryOrientation: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.orientation
          : null,
        secondaryPreviewOffsetDirection: orthogonalDelta && orthogonalDelta.targetBackLineEntry
          ? orthogonalDelta.targetBackLineEntry.previewOffsetDirection
          : null,
      },
  };
}

function isTargetSideLineAvailableForSnap(movingShape, targetShape, targetSideLineEntry) {
  if (
    !movingShape
    || !movingShape.shapeAutoShuffle
    || typeof movingShape.shapeAutoShuffle.checkForAdjacentShape !== 'function'
  ) {
    return true;
  }

  return !movingShape.shapeAutoShuffle.checkForAdjacentShape(
    targetShape,
    targetSideLineEntry.points.from,
    targetSideLineEntry.line
  );
}

function buildSideLineSnapCandidate({
  movingShape,
  movingBounds,
  candidatePosition,
  targetShape,
  targetBounds,
  movingSideLineEntry,
  targetSideLineEntry,
  movingDelta,
}) {
  const orientation = targetSideLineEntry.orientation;

  if (movingSideLineEntry.orientation !== orientation) {
    return null;
  }

  if (!isTargetSideLineAvailableForSnap(movingShape, targetShape, targetSideLineEntry)) {
    return null;
  }

  const targetPoints = targetSideLineEntry.points;
  const movedPoints = moveLinePoints(movingSideLineEntry.points, movingDelta);
  const targetLineValue = getLineAxisValue(targetPoints, orientation);
  const movedLineValue = getLineAxisValue(movedPoints, orientation);
  const snapDelta = targetLineValue - movedLineValue;
  let nextDelta = orientation === 'vertical'
    ? { x: movingDelta.x + snapDelta, y: movingDelta.y }
    : { x: movingDelta.x, y: movingDelta.y + snapDelta };
  const secondaryAlignmentDelta = getSecondaryBoundsAlignmentDelta({
    movingBounds,
    targetBounds,
    movingDelta,
    nextDelta,
    primaryOrientation: orientation,
  });

  if (secondaryAlignmentDelta) {
    nextDelta = {
      x: nextDelta.x + secondaryAlignmentDelta.x,
      y: nextDelta.y + secondaryAlignmentDelta.y,
    };
  }

  const nextMovedPoints = moveLinePoints(movingSideLineEntry.points, nextDelta);

  if (!getLineOverlapForOrientation(nextMovedPoints, targetPoints, orientation)) {
    return null;
  }

  return {
    type: CABINET_PLACEMENT_TYPE.SNAP,
    rotationDegrees: targetShape.rotatedDegrees || 0,
    position: {
      x: candidatePosition.x + nextDelta.x - movingDelta.x,
      y: candidatePosition.y + nextDelta.y - movingDelta.y,
    },
    insertPoint: targetSideLineEntry.points.from,
    hoveredLine: targetSideLineEntry.line,
    targetShape,
    targetBounds,
    movingShape,
    movingBounds,
    candidatePosition,
    distance: Math.abs(snapDelta),
    delta: {
      x: nextDelta.x - movingDelta.x,
      y: nextDelta.y - movingDelta.y,
    },
    edge: orientation === 'vertical'
      ? {
        orientation,
        x: targetLineValue,
        role: 'side-wall',
      }
      : {
        orientation,
        y: targetLineValue,
        role: 'side-wall',
      },
  };
}

function getOrthogonalBackSnapDelta({
  movingBackLineEntry,
  targetBackLineEntry,
  movingBackLineEntries,
  targetBackLineEntries,
  nextDelta,
}) {
  const primaryOrientation = targetBackLineEntry.orientation;
  const orthogonalMovingLines = movingBackLineEntries
    .filter(entry => entry.orientation && entry.orientation !== primaryOrientation && entry !== movingBackLineEntry);
  const orthogonalTargetLines = targetBackLineEntries
    .filter(entry => entry.orientation && entry.orientation !== primaryOrientation && entry !== targetBackLineEntry);
  let closestDelta = null;

  orthogonalMovingLines.forEach((movingEntry) => {
    orthogonalTargetLines.forEach((targetEntry) => {
      const movedPoints = moveLinePoints(movingEntry.points, nextDelta);
      const targetLineValue = getLineAxisValue(targetEntry.points, targetEntry.orientation);
      const movedLineValue = getLineAxisValue(movedPoints, movingEntry.orientation);
      const snapDelta = targetLineValue - movedLineValue;
      if (Math.abs(snapDelta) > CABINET_CORNER_BACK_SNAP_OFFSET) {
        return;
      }

      const candidateDelta = movingEntry.orientation === 'vertical'
        ? { x: snapDelta, y: 0 }
        : { x: 0, y: snapDelta };
      const movedCandidatePoints = moveLinePoints(movingEntry.points, {
        x: nextDelta.x + candidateDelta.x,
        y: nextDelta.y + candidateDelta.y,
      });

      if (!getLineOverlapForOrientation(movedCandidatePoints, targetEntry.points, targetEntry.orientation)) {
        return;
      }

      if (
        closestDelta
        && Math.abs(closestDelta.x + closestDelta.y) <= Math.abs(candidateDelta.x + candidateDelta.y)
      ) {
        return;
      }

      closestDelta = {
        ...candidateDelta,
        targetBackLineEntry: targetEntry,
      };
    });
  });

  return closestDelta;
}

function getWallBackEdgeCandidates(
  movingShape,
  movingBounds,
  candidatePosition,
  targetShape,
  targetBounds,
  compatibleShapeEntries
) {
  if (!canSnapCabinetBackToBack(movingShape, targetShape)) {
    return [];
  }

  return withShapeRotation(movingShape, targetShape.rotatedDegrees || 0, () => {
    const debugLogger = createCabinetSnapDebugLogger();
    const movingWallLineEntries = getCabinetLineEntriesByRoles(movingShape, [
      CABINET_LINE_ROLE.BACK,
      CABINET_LINE_ROLE.SIDE,
    ]);
    const targetBackLineEntries = getCabinetLineEntries(targetShape, CABINET_LINE_ROLE.BACK);
    const movingDelta = {
      x: candidatePosition.x - movingShape.getPosition().x,
      y: candidatePosition.y - movingShape.getPosition().y,
    };

    if (!movingWallLineEntries.length || !targetBackLineEntries.length) {
      if (debugLogger) {
        debugLogger.addNoLines({
          movingShape,
          targetShape,
          movingLineCount: movingWallLineEntries.length,
          targetBackLineCount: targetBackLineEntries.length,
        });
        debugLogger.flush();
      }
      return [];
    }

    const candidates = [];

    movingWallLineEntries.forEach((movingBackLineEntry) => {
      targetBackLineEntries.forEach((targetBackLineEntry) => {
        if (targetBackLineEntry.orientation !== movingBackLineEntry.orientation) {
          if (debugLogger) {
            debugLogger.addOrientationMismatch({
              movingShape,
              targetShape,
              movingLineEntry: movingBackLineEntry,
              targetLineEntry: targetBackLineEntry,
            });
          }
          return;
        }

        candidates.push(buildWallBackSnapCandidate({
          movingShape,
          movingBounds,
          candidatePosition,
          targetShape,
          targetBounds,
          movingBackLineEntry,
          targetBackLineEntry,
          movingBackLineEntries: movingWallLineEntries,
          targetBackLineEntries,
          compatibleShapeEntries,
          movingDelta,
          debugLogger,
        }));
      });
    });

    if (debugLogger) {
      debugLogger.flush({
        movingShapeId: movingShape.id,
        targetShapeId: targetShape.id,
        movingLineCount: movingWallLineEntries.length,
        targetBackLineCount: targetBackLineEntries.length,
        candidateCount: candidates.filter(Boolean).length,
      });
    }

    return candidates.filter(Boolean);
  });
}

function canSnapCabinetSideLines(movingShape, targetShape) {
  return (
    isSameCabinetFamily(movingShape, targetShape)
    && canResolveCabinetPlacement(movingShape, targetShape)
  ) || (
    isWallCabinetShape(movingShape)
    && isWallCabinetShape(targetShape)
  );
}

function getSideLineCandidates(
  movingShape,
  movingBounds,
  candidatePosition,
  targetShape,
  targetBounds
) {
  if (!canSnapCabinetSideLines(movingShape, targetShape)) {
    return [];
  }

  return withShapeRotation(movingShape, targetShape.rotatedDegrees || 0, () => {
    const movingSideLineEntries = getCabinetLineEntries(movingShape, CABINET_LINE_ROLE.SIDE);
    const targetSideLineEntries = getCabinetLineEntries(targetShape, CABINET_LINE_ROLE.SIDE);
    const movingDelta = {
      x: candidatePosition.x - movingShape.getPosition().x,
      y: candidatePosition.y - movingShape.getPosition().y,
    };

    if (!movingSideLineEntries.length || !targetSideLineEntries.length) {
      return [];
    }

    const candidates = [];

    movingSideLineEntries.forEach((movingSideLineEntry) => {
      targetSideLineEntries.forEach((targetSideLineEntry) => {
        candidates.push(buildSideLineSnapCandidate({
          movingShape,
          movingBounds,
          candidatePosition,
          targetShape,
          targetBounds,
          movingSideLineEntry,
          targetSideLineEntry,
          movingDelta,
        }));
      });
    });

    return candidates.filter(Boolean);
  });
}

/**
 * Returns the closest valid snap/insert candidate for a cabinet drag.
 *
 * The returned position is the preview position for the moving cabinet. If the
 * candidate is an insertion, hoveredLine/insertPoint are also provided so commit
 * can call ShapeAutoShuffle instead of duplicating 2D shuffle logic here.
 * This keeps 3D as a placement detector while the 2D workspace remains the
 * source of truth for snapping groups, rotations, and shuffle side effects.
 */
export function resolveCabinetPlacement(
  movingShape,
  candidatePosition,
  startShapePosition,
  startShapeBounds,
  placementContext = null
) {
  if (!movingShape || !candidatePosition || !startShapePosition || !startShapeBounds) {
    return null;
  }

  const threshold = Math.min(sqftConfig.SNAP_OFFSET || CABINET_PLACEMENT_SNAP_OFFSET, CABINET_PLACEMENT_SNAP_OFFSET);
  const movingBounds = moveBounds(startShapeBounds, {
    x: candidatePosition.x - startShapePosition.x,
    y: candidatePosition.y - startShapePosition.y,
  });
  const candidates = [];
  const compatibleShapeEntries = placementContext && placementContext.compatibleShapeEntries
    ? placementContext.compatibleShapeEntries
    : createCabinetPlacementContext(movingShape).compatibleShapeEntries;

  compatibleShapeEntries.forEach(({ shape: targetShape, bounds: targetBounds }) => {
    const shouldUseRegularRunPlacement = isSameCabinetFamily(movingShape, targetShape)
      && canResolveCabinetPlacement(movingShape, targetShape);

    candidates.push(
      ...getWallBackEdgeCandidates(
        movingShape,
        movingBounds,
        candidatePosition,
        targetShape,
        targetBounds,
        compatibleShapeEntries
      )
    );
    candidates.push(
      ...getSideLineCandidates(
        movingShape,
        movingBounds,
        candidatePosition,
        targetShape,
        targetBounds
      )
    );

    if (!shouldUseRegularRunPlacement) {
      return;
    }

    candidates.push(
      ...getVerticalEdgeCandidates(movingShape, movingBounds, candidatePosition, targetShape, targetBounds),
      ...getHorizontalEdgeCandidates(movingShape, movingBounds, candidatePosition, targetShape, targetBounds)
    );
  });

  return candidates
    .filter(candidate => candidate.distance <= threshold)
    .map(candidate => ({
      ...candidate,
      overlapArea: getCandidateOverlapArea(candidate, compatibleShapeEntries),
    }))
    .sort((first, second) => (
      first.overlapArea - second.overlapArea
      || getPlacementPriority(first) - getPlacementPriority(second)
      || first.distance - second.distance
    ))[0] || null;
}

/**
 * Captures the shape bounds at drag start so preview calculations can stay pure
 * while the real workspace shape remains unchanged until pointerup.
 */
export function getShapeStartBounds(shape) {
  return sqftShapeManager.getShapeBoundsOnWorkspace(shape);
}
